import { calculateDiscountedAmount } from "@/components/Checkout";
import { toCheckoutStoreData } from "@/components/Checkout/utils/checkoutAdapter";
import { calculateFinalPrice, calculateProductDiscount, findOrderItemPrice } from "@/components/Checkout/utils/payUtils";
import { create } from "zustand";
import { useCouponStore } from "./useCouponStore";
// 초기 상태 (마운트 시점 및 reset 시 사용)
const initialState = {
    orderItems: [],
    userCoupons: [],
    priceInfo: {
        originalPrice: 0,
        baseProductDiscount: 0,
        couponDiscount: 0,
        productDiscount: 0,
        deliveryFee: 0,
        remoteAreaFee: 0,
        deliveryDiscount: 0,
        usedCash: 0,
        usedPoint: 0,
        finalPrice: 0,
        finalPriceWithCoupon: 0,
        unusedPointPrice: 0,
        isUnusedPoint: false
    },
    myCashBalance: 0,
    myPointBalance: 0,
    isAlwaysUseCash: false,
    deliveryInfo: {
        basicDeliveryCharge: 0,
        remoteAreaFee: 0,
        deliveryFeeCouponAmount: 0
    }
};
const useCheckoutStore = create((set, get)=>{
    return {
        // 초기 상태
        ...initialState,
        // 스토어 초기화: 초기 상태로 되돌려 다음 진입 시 stale 금액으로 위젯이 렌더되는 것을 방지
        resetStore: ()=>{
            set({
                ...initialState
            });
        },
        // 1. 초기화 (컴포넌트 마운트 시)
        initializeStore: (response, type)=>{
            const state = get();
            const updatedData = toCheckoutStoreData(response, type);
            // response에서 isUnusedPoint와 unusedPointPrice 추출 (usePayment에서 전달한 값)
            const isUnusedPoint = "isUnusedPoint" in response && typeof response.isUnusedPoint === "boolean" ? response.isUnusedPoint : false;
            const unusedPointPrice = "unusedPointPrice" in response && typeof response.unusedPointPrice === "number" ? response.unusedPointPrice : 0;
            // 기존 쿠폰 적용 상태 보존
            const preservedOrderItems = updatedData.orderItems.map((newItem)=>{
                const existingItem = state.orderItems.find((item)=>item.id === newItem.id);
                if (existingItem === null || existingItem === void 0 ? void 0 : existingItem.isCouponApplied) {
                    return {
                        ...newItem,
                        isCouponApplied: true,
                        appliedCouponId: existingItem.appliedCouponId,
                        discountedPrice: existingItem.discountedPrice
                    };
                }
                return newItem;
            });
            // 배송비 정보 업데이트 (배송비 쿠폰은 새 주문 시 초기화)
            const deliveryInfo = {
                basicDeliveryCharge: updatedData.priceInfo.deliveryFee || 0,
                remoteAreaFee: updatedData.priceInfo.remoteAreaFee || 0,
                deliveryFeeCouponId: undefined,
                deliveryFeeCouponAmount: 0
            };
            set({
                orderItems: preservedOrderItems,
                userCoupons: updatedData.userCoupons,
                deliveryInfo,
                priceInfo: {
                    ...state.priceInfo,
                    originalPrice: updatedData.priceInfo.originalPrice,
                    // 쿠폰이 아닌 상품 자체 할인은 재계산으로 복원할 수 없어 여기서 보존한다
                    baseProductDiscount: updatedData.priceInfo.baseProductDiscount || 0,
                    deliveryFee: deliveryInfo.basicDeliveryCharge,
                    remoteAreaFee: deliveryInfo.remoteAreaFee,
                    savedPoint: updatedData.priceInfo.savedPoint,
                    unusedPointPrice,
                    isUnusedPoint,
                    usedCash: 0,
                    usedPoint: 0
                }
            });
            // 가격 재계산
            get()._recalculatePrices();
        },
        // 2. 상품 쿠폰 적용
        applyProductCoupon: (itemId, couponId, discountedPrice)=>{
            const state = get();
            const updatedItems = state.orderItems.map((item)=>item.id === itemId ? {
                    ...item,
                    isCouponApplied: true,
                    appliedCouponId: couponId,
                    discountedPrice
                } : item);
            set({
                orderItems: updatedItems
            });
            get()._recalculatePrices();
        },
        // 3. 상품 쿠폰 제거
        removeProductCoupon: function(itemId) {
            let skipRecalculate = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
            const state = get();
            const updatedItems = state.orderItems.map((item)=>item.id === itemId ? {
                    ...item,
                    isCouponApplied: false,
                    appliedCouponId: null,
                    discountedPrice: findOrderItemPrice(item)
                } : item);
            set({
                orderItems: updatedItems
            });
            if (!skipRecalculate) {
                get()._recalculatePrices();
            }
        },
        // 4. 배송비 쿠폰 적용
        applyDeliveryCoupon: (couponId, discountAmount)=>{
            set((state)=>({
                    deliveryInfo: {
                        ...state.deliveryInfo,
                        deliveryFeeCouponId: couponId,
                        deliveryFeeCouponAmount: discountAmount
                    }
                }));
            get()._recalculatePrices();
        },
        // 5. 배송비 쿠폰 제거
        removeDeliveryCoupon: ()=>{
            set((state)=>({
                    deliveryInfo: {
                        ...state.deliveryInfo,
                        deliveryFeeCouponId: null,
                        deliveryFeeCouponAmount: 0
                    }
                }));
            get()._recalculatePrices();
        },
        // 6. 캐시 사용 업데이트
        updateUsedCash: (amount)=>{
            set((state)=>({
                    priceInfo: {
                        ...state.priceInfo,
                        usedCash: amount
                    }
                }));
            get()._recalculatePrices();
        },
        // 7. 포인트 사용 업데이트
        updateUsedPoint: (amount)=>{
            set((state)=>({
                    priceInfo: {
                        ...state.priceInfo,
                        usedPoint: amount
                    }
                }));
            get()._recalculatePrices();
        },
        // 8. 배송비 업데이트 (주소 변경 시)
        updateDeliveryFee: (basicCharge, remoteAreaFee)=>{
            set((state)=>({
                    deliveryInfo: {
                        ...state.deliveryInfo,
                        basicDeliveryCharge: basicCharge,
                        remoteAreaFee: remoteAreaFee
                    },
                    priceInfo: {
                        ...state.priceInfo,
                        deliveryFee: basicCharge,
                        remoteAreaFee: remoteAreaFee
                    }
                }));
            get()._recalculatePrices();
        },
        // 9. 모든 쿠폰 제거
        clearAllCoupons: ()=>{
            const state = get();
            // 모든 상품 쿠폰 제거
            const clearedOrderItems = state.orderItems.map((item)=>({
                    ...item,
                    isCouponApplied: false,
                    appliedCouponId: null,
                    discountedPrice: findOrderItemPrice(item)
                }));
            // POD 쿠폰 스토어 초기화
            useCouponStore.getState().clearAllCoupons();
            set({
                orderItems: clearedOrderItems
            });
            get()._recalculatePrices();
        },
        // 10. 내 캐시 잔액 업데이트
        updateMyCashBalance: (amount)=>{
            set({
                myCashBalance: amount
            });
        },
        // 11. 내 포인트 잔액 업데이트
        updateMyPointBalance: (amount)=>{
            set({
                myPointBalance: amount
            });
        },
        // 12. 항상 전액 사용 체크 여부 업데이트
        updateIsAlwaysUseCash: (value)=>{
            set({
                isAlwaysUseCash: value
            });
        },
        // 내부 헬퍼: 가격 재계산
        _recalculatePrices: ()=>{
            const state = get();
            // 쿠폰 할인은 매번 다시 계산하고, 상품 자체 할인은 초기화 때 받은 값을 그대로 쓴다.
            // productDiscount는 둘의 합계이므로 반복 호출해도 누적되지 않는다.
            const couponDiscount = calculateProductDiscount(state.orderItems);
            const baseProductDiscount = state.priceInfo.baseProductDiscount || 0;
            const productDiscount = baseProductDiscount + couponDiscount;
            // 최종 가격 계산
            // 쿠폰적용을 하고 나서, 포인트 사용 불가 상품의 가격을 합산하여 저장
            const unusedPointPrice = state.orderItems.filter((item)=>item.isPointUnusable).reduce((acc, item)=>acc + item.discountedPrice, 0);
            // 포인트 사용 불가 여부: 모든 상품이 포인트 사용 불가인 경우에만 true
            // (일부 상품만 포인트 불가인 경우, 나머지 상품에 대해서는 포인트 사용 가능해야 함)
            const isUnusedPoint = state.orderItems.length > 0 && state.orderItems.every((item)=>item.isPointUnusable);
            // toss pgAmount 계산
            const finalPrice = calculateFinalPrice({
                originalPrice: state.priceInfo.originalPrice,
                productDiscount,
                deliveryFee: state.priceInfo.deliveryFee,
                remoteAreaFee: state.priceInfo.remoteAreaFee,
                deliveryDiscount: state.deliveryInfo.deliveryFeeCouponAmount,
                usedCash: state.priceInfo.usedCash,
                usedPoint: state.priceInfo.usedPoint
            });
            // 아래 finalPriceWithCoupon, sumAllDeliveryFeeFinalPrice 는 be에서 필요한 캐시와 포인트 제외한 가격입니다.
            const finalPriceWithCoupon = calculateDiscountedAmount({
                price: state.priceInfo.originalPrice,
                discount: productDiscount
            });
            const sumAllDeliveryFeeFinalPrice = parseInt(finalPriceWithCoupon) + (state.deliveryInfo.basicDeliveryCharge + state.deliveryInfo.remoteAreaFee) - state.deliveryInfo.deliveryFeeCouponAmount;
            set((state)=>({
                    priceInfo: {
                        ...state.priceInfo,
                        finalPriceWithCoupon: sumAllDeliveryFeeFinalPrice,
                        couponDiscount,
                        productDiscount,
                        deliveryDiscount: state.deliveryInfo.deliveryFeeCouponAmount,
                        finalPrice,
                        unusedPointPrice: unusedPointPrice || 0,
                        isUnusedPoint
                    }
                }));
        }
    };
});
export default useCheckoutStore;
